// BRIEF 86 PHASE 2, STEP 4 (run by brief 90 §5) — the Tasks pane, once.
//
// 458 lines in `CaseView`, 455 in `CollectiveCaseView`, 88% normalised. As with
// steps 1-3, the merge only became possible after the pair's real divergences
// were resolved — here seven of them, every one "one side corrected, the other
// never told": the Eisenhower quadrant saving through a legacy local `svTk`, the
// tap-through guard split across both files, one empty state instead of two, the
// missing reminder default, the matrix tooltip's two i18n keys, and the empty
// states rendering under an open matrix.
//
// EXTRACTED, NOT REWRITTEN (rule 3). The body is CaseView's block lifted
// verbatim and de-indented, with FOUR parameterisations, all discriminators or
// host handles:
//
//   the task filter    t.caseId  vs  t.ccId
//   the task modal     om()  vs  globalOm(), taken as `openTaskModal`
//   the matter list    ca  vs  cc  (what TaskMatrix resolves a row's parent in)
//   `openModal`        the same handle, passed on to CaseTaskRow
//
// THE PROP LIST IS LONG — thirty-odd — AND THAT IS THE HONEST SHAPE. This pane
// owns seven pieces of view state (expanded, locked, matrix, my, page, sort,
// statusFilt) plus their setters, and a component cannot hold state its two
// hosts already hold and pass to other things. Threading it is what makes the
// definition single; hiding the thread by re-deriving state inside would make
// two sources of truth, which is the defect this whole phase is removing.
import React from "react"; // react/react-in-jsx-scope is counted per JSX element; the rule's own remedy (182 C.1)
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, ReactNode } from "react"; // 195 §A — TaskTableHead's two optional closures
import { ChevronDown, ChevronRight } from "lucide-react";
import CaseTaskRow from "./CaseTaskRow";
import { TaskMatrix } from "../TaskMatrix";
import { Grid2x2, ChevronsUpDown, ChevronsDownUp, SquareKanban } from "lucide-react";
import BentoDialog from "./dialogs/BentoDialog"; // 190 §C surface 2
import { BentoPicker, BentoPickerAnchor } from "./BentoBoard"; // 190 [I] — the picker beside the icon; round 9 [AS] — or below it
import { useBento } from "../hooks/useBento";
import { usePermissions } from "../hooks/usePermissions";
import { queueScrollToTk } from "../utils";
import { SectionHeader, AddButton, LockButton, MyAllToggle, SegmentedControl, OPEN_MODE_GLOW, OPEN_MODE_ICON, ListNotLoaded } from "./SharedUI"; // [AA] — the lock's glow; 210 §1
import Pager from "./Pager";
import { pageOf } from "../hooks/usePager";
import { pname, eisenQuadrant } from "../utils";
import type { LevelList } from "../utils";
import { FS, RAD, Z } from "../lib/tokens";
import { TYPE_COL_W } from "./TypeTagBox"; // 118 §8
import Table from "./Table";

// The read-sets this pane uses. Partial by design: a task row carries dozens of
// fields and this pane reads a handful.
type Person = { id: number; coId?: number };
type Task = {
  id: number;
  type?: string | null;
  caseId?: number | null;
  ccId?: number | null;
  status?: string | null;
  dueDate?: string | null;
  assignedCoId?: number | null;
  caseEisenhower?: string | null;
  [k: string]: unknown;
};

// Thirty-odd props, written out. The pane owns seven pieces of view state and
// their setters, and a component cannot hold state its two hosts already pass to
// other things — threading it is what makes the definition single. Re-deriving
// it inside would create two sources of truth, which is the defect this phase
// exists to remove.
type Props = {
  kind: "case" | "cc";
  cs: { id: number; caseCollaborators?: { coId: number }[]; [k: string]: unknown };
  co: Person[];
  tk: Task[];
  sTk: unknown;
  s: (...a: unknown[]) => void;
  SK: Record<string, string>;
  ca: unknown[];
  cc: unknown[];
  currentUser?: number | null;
  tkTypes: unknown;
  itemLevelList?: LevelList;
  itemLevelBase: (list: LevelList) => string | null;
  itemLevelOrder: (list: LevelList) => Record<string, number>;
  _ITEM_LEVEL?: unknown;
  tkExpanded: Set<unknown>;
  setTkExpanded: (v: Set<unknown> | ((s: Set<unknown>) => Set<unknown>)) => void;
  tkLocked: boolean;
  setTkLocked: (fn: (v: boolean) => boolean) => void;
  tkMatrix: boolean;
  setTkMatrix: (v: boolean | ((v: boolean) => boolean)) => void;
  /* 190 glance round 2 [A] — the case Bento's open state, the host's (beside
     `tkMatrix`): the toggle and the pane are here, the Agenda section hides
     its body under the pane (`covered`). */
  bentoOpen: boolean;
  /** 190 round 6 [T] — App's `userMatrixQuadrant(task)`: the case Bento's card edge is the task's quadrant colour. */
  userMatrixQuadrant?: (t: unknown) => string;
  setBentoOpen: (v: boolean | ((v: boolean) => boolean)) => void;
  tkMy: boolean;
  setTkMy: (v: boolean | ((v: boolean) => boolean)) => void;
  tkPage: number;
  setTkPage: (v: number) => void;
  // Keyed BY MATTER ID — each case remembers its own task sort.
  // Keyed BY MATTER ID — each case remembers its own task sort. `d` is the
  // direction and is always set when a sort exists (the cycler writes both).
  tkSort: Record<number, { k: string; d: number } | null>;
  setTkSort: (v: unknown | ((s: unknown) => unknown)) => void;
  tkStatusFilt: string;
  setTkStatusFilt: (v: string) => void;
  tkRef: { current: Task[] };
  canEditTasks?: boolean;
  canEditOthersTasks?: boolean;
  _isRPHere?: boolean;
  _permViewer?: { isAdmin?: boolean } | null;
  permIsAdmin?: boolean;
  activeTimers?: unknown;
  startTimer?: unknown;
  confirmAndUndo: (o: {
    label?: unknown;
    onConfirm: () => void;
    undo?: { snapshot: unknown; restore: (snap: Task) => void } | null;
    toastName?: unknown;
    archive?: unknown;
  }) => void;
  svTkData: (t: Task) => void;
  _navCase: (kind: string, id: number) => void;
  nav: (kind: string, id: number) => void;
  onSubtaskDone?: unknown;
  openTaskModal: (kind: string, payload: Record<string, unknown>) => void;
  sCol: (k: string) => boolean;
  tog: (k: string) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
  toast: (m: string) => void;
  TkStatusTag: unknown;
  headerStyle?: Record<string, unknown>;
};


/* 195 §A — THE TASKS TABLE'S HEADER, EXTRACTED so the Archive can render an
   archived task in the SAME table the matter renders it in (Laurent,
   2026-09-18: "the exact same items as in CV/CCV … just reuse what exists").
   Moved out of the section's render verbatim (SESSION_RULES rule 3: moved, not
   bettered); what the move needed and nothing more — the two sort closures
   become optional props, so a host with no sort (the Archive) renders the same
   cells with no arrow and no pointer. `fur` is the host's furniture predicate
   (the same `viewer === null || can(k)` both rows carry), so a hidden column
   goes header and cells together here as it does in the section. Hookless and
   at module scope (Bug-A); `t` is the section's own translation function. */
export function TaskTableHead({
  t,
  fur,
  showLevel,
  sort,
  onSort,
}: Readonly<{
  t: Props["t"];
  fur: ReturnType<typeof usePermissions>["can"];
  showLevel: boolean;
  /** the host's current sort, when it sorts; absent → no arrow */
  sort?: { k: string; d: number } | null;
  /** the sort cycle for a column, when the host sorts; absent → no click, no pointer */
  onSort?: Dispatch<string>;
}>) {
  /* the indicator the section drew inline, moved with the cells that read
     it: the arrow for the sorted column, a faint one for the rest */
  const ind = (k: string): ReactNode =>
    !onSort ? null : sort && sort.k === k ? (
      <span style={{ marginLeft: 3, fontSize: FS.s8 }}>
        {sort.d === 1 ? "\u25B2" : "\u25BC"}
      </span>
    ) : (
      <span style={{ marginLeft: 3, fontSize: FS.s8, opacity: 0.35 }}>
        {"\u25B6"}
      </span>
    );
  const cursor = onSort ? "pointer" : undefined;
  const click = (k: string) => (onSort ? () => onSort(k) : undefined);
  return (
      <thead>
        <tr>
          {/* 190 §F.2 — each header cell under its column's
              furniture key, the row's own (CaseTaskRow.tsx). */}
          {fur("furniture.task.type") && (
          <th
            /* BRIEF 118 §8 — 38 → `TYPE_COL_W`. The 38 governed
               nothing: this header's own content ("Type" + the
               sort indicator) renders 60, and 60 is what the
               column has always measured. Declaring it changes no
               pixel here and gives the event cards a number to
               cite. */
            style={{
              width: TYPE_COL_W,
              cursor,
              userSelect: "none",
            }}
            onClick={click("type")}
          >
            {t("case.tk.col.type", { fallback: "Type" })}
            {ind("type")}
          </th>
          )}
          {fur("furniture.task.eisenhower") && (
          <th
            style={{
              width: 22,
              padding: "6px 2px",
              cursor,
              userSelect: "none",
            }}
            onClick={click("eisen")}
          >
            {ind("eisen")}
          </th>
          )}
          {fur("furniture.task.assignees") && (
          <th
            style={{
              width: 28,
              padding: "7px 2px",
              cursor,
              userSelect: "none",
            }}
            onClick={click("assigned")}
          >
            {ind("assigned")}
          </th>
          )}
          {fur("furniture.task.timer") && <th style={{ padding: "7px 2px" }}></th>}
          {fur("furniture.task.subtasks") && (
          <th
            style={{
              width: 36,
              padding: "7px 2px",
              textAlign: "center",
            }}
          ></th>
          )}
          {/* BRIEF 118 §5 [RULED] — AN UNSET LEVEL YIELDS ITS
              WIDTH TO THE TITLE, and 117 §1's own finding is why
              this is a TABLE change and not a cell one: this
              header's sort indicator is the column's width, so
              emptying the cells bought nothing. MEASURED on watt
              before: the column renders 29.5px whether every row
              carries a level or none does.
              THE COLUMN GOES when no row on the PAGE has one —
              header included, because the header IS the floor.
              THE COST, ENUMERATED: sorting by level goes with it.
              That is the honest trade rather than a loss: the
              affordance sorts a column in which every value is
              absent. When SOME rows carry a level the column
              stays, its empty cells stay empty (117 §1), and
              nothing about the sort changes. */}
          {showLevel && fur("furniture.task.level") && (
          <th
            style={{
              width: 22,
              cursor,
              userSelect: "none",
            }}
            onClick={click("priority")}
          >
            {ind("priority")}
          </th>
          )}
          <th
            style={{
              width: "100%",
              cursor,
              userSelect: "none",
            }}
            onClick={click("title")}
          >
            {t("case.tk.col.title", { fallback: "Title" })}
            {ind("title")}
          </th>
          {fur("furniture.task.status") && (
          <th
            style={{
              width: "1px",
              padding: "7px 0 7px 2px",
              whiteSpace: "nowrap",
              cursor,
              userSelect: "none",
            }}
            onClick={click("status")}
          >
            {t("case.tk.col.status", {
              fallback: "Status",
            })}
            {ind("status")}
          </th>
          )}
          {fur("furniture.task.due") && (
          <th
            style={{
              width: "1px",
              textAlign: "right",
              cursor,
              userSelect: "none",
              padding: "7px 4px 7px 4px",
              whiteSpace: "nowrap",
            }}
            onClick={click("dueDate")}
          >
            {t("case.tk.col.due", { fallback: "Due" })}
            {ind("dueDate")}
          </th>
          )}
          <th></th>
        </tr>
      </thead>
  );
}

export default function CaseTasksSection(props: Props) {
  const {
    kind, cs, co, tk, sTk, s, SK, ca, cc, currentUser, tkTypes,
    itemLevelList, itemLevelBase, itemLevelOrder, _ITEM_LEVEL,
    tkExpanded, setTkExpanded, tkLocked, setTkLocked, tkMatrix, setTkMatrix,
    bentoOpen, setBentoOpen,
    userMatrixQuadrant,
    tkMy, setTkMy, tkPage, setTkPage, tkSort, setTkSort,
    tkStatusFilt, setTkStatusFilt, tkRef,
    canEditTasks, canEditOthersTasks, permIsAdmin, _isRPHere, _permViewer,
    activeTimers, startTimer, confirmAndUndo, svTkData, _navCase, nav,
    onSubtaskDone, openTaskModal, sCol, tog, t, toast,
    // A PROP in both views, not an import — CaseView.tsx:618 and
    // CollectiveCaseView.tsx:2101 both receive it from App.
    TkStatusTag,
    headerStyle,
  } = props;
  const isCc = kind === "cc";
  /* 110 §1h — ONE toggle (enumerated: a second affordance would be a second
     thing to scan for; the icon flips with the state) that opens EVERY row's
     three drawers for one detailed sweep and closes them again. Subtasks are
     host state (tkExpanded) and are set directly; docs/notes are row-local,
     so the rows subscribe to this {seq, open} tick — the smallest broadcast
     that remounts nothing. */
  const [drawersCast, setDrawersCast] = useState({ seq: 0, open: false });
  /* 190 §C surface 2 — this case's Bento, beside the matrix icon: that case's
     tasks, FIRM-WIDE placements. 190 glance (ii) made it an IN-PAGE PANEL;
     round 2 [A] CORRECTS the frame, RULED, Laurent's words: "the idea was to
     KEEP Events and Task sections and filets as-is, just show a pane which
     would cover the tasks and event items themselves (UNDER the section
     titles and filters)". So BOTH section headers stay exactly where they
     are (titles, filter rows, locks — and the Bento icon, which never moves,
     "so one can tick/untick for a quick glance"); the pane is ONE block in
     this section's body slot that extends LEFT across the column gap over
     the Agenda section's body (`.bentoPane`, App.css: `width: calc(200% +
     16px)`, `margin-left: calc(-100% - 16px)` — the grid's two `1fr` columns
     and its 16px column gap), while the Agenda section renders no body
     (`covered`, host state beside `tkMatrix`). MEASURED at 1600/xlarge: both
     headers 53.2px, both bodies starting at 517.8 — but a header wraps on
     its own width, so `bentoPaneTop` re-measures the Agenda header's bottom
     against this body's top and pads the difference (in CSS px — a rect is
     visual px under the root zoom, the 142 family). A card click closes it
     and scrolls to the row. */
  const closeBento = useCallback(() => { setBentoOpen(false); }, [setBentoOpen]);
  const paneRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!bentoOpen) return undefined;
    const pane = paneRef.current;
    const grid = pane ? pane.closest(".csGrid") : null;
    const agHdr = grid ? grid.querySelector<HTMLElement>(".csSec-agenda .csSecHdr") : null;
    if (!pane || !agHdr) return undefined;
    const place = () => {
      pane.style.marginTop = "";
      const zoom = parseFloat(document.documentElement.style.zoom) || 1;
      const delta = (agHdr.getBoundingClientRect().bottom - pane.getBoundingClientRect().top) / zoom;
      if (delta > 0.5) pane.style.marginTop = `${Math.ceil(delta)}px`;
    };
    place();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(place);
    ro.observe(agHdr);
    return () => { ro.disconnect(); };
  }, [bentoOpen]);
  const { can: surfaceCan, viewer: permViewer } = usePermissions();
  const canBento = surfaceCan("surface.bento.case");
  /* 190 [I] — the case Bento's state is THIS host's: the picker sits right of
     the icon in the header row above, and the pane takes the state down. */
  const caseBento = useBento("case", "task", bentoOpen && canBento);
  /* 190 §F.2 — the furniture keys, on the HEADER cells: the same rule the row
     applies (CaseTaskRow.tsx `fur`), so a column goes header and cells
     together — the load window keeps every cell. 191 (1): an unwritten key
     reads SHOWN (the server fills it, CaseTaskRow.tsx `fur`); 191 (2): the
     title and actions headers carry no key. */
  const fur = (k: string): boolean => permViewer === null || surfaceCan(k);
  /* 190 §E.1 — the case matrix carries its own key too (`surface.eisenhower.case`). */
  const canMatrix = surfaceCan("surface.eisenhower.case");
  return (
    <div
      className="csSec-tasks"
      style={{ flex: "1 1 400px", minWidth: 0 }}
      data-secollapsed={sCol("tasks") ? "1" : "0"}
    >
      {(() => {
        const ctk = (tk || []).filter((t: Task) => (isCc ? t.ccId === cs.id : t.caseId === cs.id));
        const caseCos = (cs.caseCollaborators || [])
          .map((x: { coId: number }) => co.find((m: Person) => m.id === x.coId))
          .filter(Boolean);
        const tkFil = ctk
          .filter(
            (t: Task) =>
              (!tkMy ||
                !currentUser ||
                t.assignedCoId === currentUser) &&
              (tkStatusFilt === "finished"
                ? t.status === "completed" ||
                  t.status === "cancelled" ||
                  t.status === "archived"
                : tkStatusFilt === "progress"
                  ? t.status === "inprogress"
                  : tkStatusFilt === "waiting"
                    ? t.status === "waiting"
                    : t.status !== "completed" &&
                      t.status !== "cancelled" &&
                      t.status !== "archived"),
          )
          .sort((a: Task, b: Task) => {
            if (!a.dueDate && !b.dueDate) return 0;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return a.dueDate.localeCompare(b.dueDate);
          });
        const __tkCur = tkSort[cs.id] || null;
        const __EI_ORD: Record<string, number> = { ui: 0, u: 1, i: 2, s: 3 };
        const __ST_ORD: Record<string, number> = {
          todo: 0,
          inprogress: 1,
          waiting: 2,
          completed: 3,
          cancelled: 4,
          archived: 5,
        };
        const __PR_ORD = itemLevelOrder(itemLevelList);
        const __PR_BASE = itemLevelBase(itemLevelList);
        const __cycleTkSort = (k: string) => {
          const cur = tkSort[cs.id];
          let next;
          if (!cur || cur.k !== k) next = { k, d: 1 };
          else if (cur.d === 1) next = { k, d: -1 };
          else next = null;
          const upd = { ...tkSort };
          if (next) upd[cs.id] = next;
          else delete upd[cs.id];
          setTkSort(upd);
        };
        const tkFilSorted = (() => {
          if (!__tkCur) return tkFil;
          const arr = [...tkFil];
          const d = __tkCur.d;
          const k = __tkCur.k;
          arr.sort((a, b) => {
            if (k === "type") {
              return d * (a.type || "").localeCompare(b.type || "");
            }
            if (k === "eisen") {
              return (
                d *
                ((__EI_ORD[eisenQuadrant(a)] ?? 99) -
                  (__EI_ORD[eisenQuadrant(b)] ?? 99))
              );
            }
            if (k === "assigned") {
              const an = (() => {
                const c = co.find((x: Person) => x.id === a.assignedCoId);
                return c ? pname(c) : "";
              })();
              const bn = (() => {
                const c = co.find((x: Person) => x.id === b.assignedCoId);
                return c ? pname(c) : "";
              })();
              return d * an.localeCompare(bn);
            }
            if (k === "priority") {
              return (
                d *
                ((__PR_ORD[String(a.priority || __PR_BASE)] || 0) -
                  (__PR_ORD[String(b.priority || __PR_BASE)] || 0))
              );
            }
            if (k === "title") {
              return d * String(a.title || "").localeCompare(String(b.title || ""));
            }
            if (k === "status") {
              return (
                d *
                ((__ST_ORD[a.status || "todo"] ?? 99) -
                  (__ST_ORD[b.status || "todo"] ?? 99))
              );
            }
            if (k === "dueDate") {
              const av = a.dueDate || "";
              const bv = b.dueDate || "";
              if (!av && !bv) return 0;
              if (!av) return 1;
              if (!bv) return -1;
              return d * av.localeCompare(bv);
            }
            return 0;
          });
          return arr;
        })();
        /* 188 D.3 — the arithmetic is `pageOf`'s (ten; `Math.max(1, ceil)` ·
           `Math.min(page, tot - 1)` · the slice — this section's own three
           expressions, identity replayed over 4,800 cases, 0 differences: the
           floor and the clamp were already here, so D.4 has nothing to change).
           The page state stays CaseView's `tkPage`. */
        const { cur: tkCurPage, tot: tkTotalPages, pageItems: tkPaged } = pageOf(tkFilSorted, tkPage);
        /* BRIEF 118 §5 — over the PAGE, not the filtered set: the table that is
           rendered is the one whose columns are in question, and a level three
           pages away cannot be seen to justify a gap here. Paging to a page that
           has one brings the column back, which is the same rule applied again
           rather than a special case. */
        const __tkShowLevel = tkPaged.some((t: Task) => !!t.priority);
        return (
          <>
            <SectionHeader
              className="tkHdr"
              title={t("case.tasks")}
              collapsible
              open={!sCol("tasks")}
              onToggle={() => tog("tasks")}
              leftContent={
                <>
                  {currentUser && (
                    <MyAllToggle
                      my={tkMy}
                      onMy={() => setTkMy(true)}
                      onAll={() => setTkMy(false)}
                      counts={{
                        my: ctk.filter(
                          (t: Task) => t.assignedCoId === currentUser,
                        ).length,
                        all: ctk.length,
                      }}
                    />
                  )}
                  <SegmentedControl
                    value={tkStatusFilt}
                    options={[
                      ["all", t("common.all")],
                      ["progress", t("case.tk.progress")],
                      ["waiting", t("case.tk.waiting")],
                      ["finished", t("case.tk.finished")],
                    ]}
                    onChange={(k: string) => {
                      setTkStatusFilt(k);
                      setTkPage(0);
                    }}
                    counts={{
                      all: ctk.filter(
                        (t: Task) =>
                          t.status !== "completed" &&
                          t.status !== "cancelled" &&
                          t.status !== "archived",
                      ).length,
                      progress: ctk.filter(
                        (t: Task) => t.status === "inprogress",
                      ).length,
                      waiting: ctk.filter((t: Task) => t.status === "waiting")
                        .length,
                      finished: ctk.filter(
                        (t: Task) =>
                          t.status === "completed" ||
                          t.status === "cancelled" ||
                          t.status === "archived",
                      ).length,
                    }}
                  />
                  {canMatrix && (
                  <button
                    type="button"
                    className="scIcHov"
                    /* BRIEF 86 PHASE 2 STEP 4 — CCV's key. One
                       tooltip, two keys, so a firm editing the wording
                       changed it on one screen — brief 59's finding.
                       `field.case_matrix` names the FIELD rather than
                       the tip, which is why it is the survivor: it is
                       the same words the modal's own label uses. */
                    title={t("field.case_matrix", {
                      fallback: "Case Matrix",
                    })}
                    /* 190 round 6 [AA] — the icon glows while its panel is
                       open: the lock's three declarations (OPEN_MODE_GLOW),
                       the way out. The `--ac`/opacity tell it wore is gone;
                       the glow is the one state. */
                    style={{
                      background: tkMatrix ? OPEN_MODE_GLOW.background : "none",
                      border: tkMatrix ? OPEN_MODE_GLOW.border : "none",
                      animation: tkMatrix ? OPEN_MODE_GLOW.animation : undefined,
                      borderRadius: RAD.r6,
                      cursor: "pointer",
                      padding: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: tkMatrix ? OPEN_MODE_ICON : "var(--tp)",
                      opacity: tkMatrix ? 1 : 0.6,
                    }}
                    onClick={() => { setBentoOpen(false); setTkMatrix((v: boolean) => !v); }}
                  >
                    <Grid2x2 size={14} />
                  </button>
                  )}
                  {/* 190 round 9 [AS] — the icon is the picker's ANCHOR: beside it where
                      the row has room, below it, flush left, where it has not (BentoBoard's
                      BentoPickerAnchor, the measured rule for all three hosts). */}
                  {canBento && (
                    <BentoPickerAnchor picker={bentoOpen ? <BentoPicker bento={caseBento} id="bento-template-case" zIndex={Z.picker} /> : null}>
                    <button
                      type="button"
                      className="scIcHov"
                      title={t("bento.case_title", { fallback: "Case Bento" })}
                      /* [AA] — glows while the case Bento is open. [Y]: the
                         icon STAYS at 14 here — the advisor's call: round 4
                         measured this header at 620 visual px of controls in
                         a 636 px row at his width, and 21 pushes the
                         expand-all toggle onto a second line; his rule at
                         the glance if he disagrees. */
                      style={{
                        background: bentoOpen ? OPEN_MODE_GLOW.background : "none",
                        border: bentoOpen ? OPEN_MODE_GLOW.border : "none",
                        animation: bentoOpen ? OPEN_MODE_GLOW.animation : undefined,
                        borderRadius: RAD.r6,
                        cursor: "pointer",
                        padding: 2,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: bentoOpen ? OPEN_MODE_ICON : "var(--tp)",
                        opacity: bentoOpen ? 1 : 0.6,
                      }}
                      onClick={() => { setTkMatrix(false); setBentoOpen((v) => !v); }}
                    >
                      <SquareKanban size={14} />
                    </button>
                    </BentoPickerAnchor>
                  )}
                  {/* 110 §1h — open/close every row's drawers (subtasks +
                      documents + notes) for one detailed sweep. One toggle;
                      the icon shows the action it would take. */}
                  <button
                    type="button"
                    title={
                      drawersCast.open
                        ? t("drawers.close_all", {
                            fallback: "Close all drawers",
                          })
                        : t("drawers.open_all", { fallback: "Open all drawers" })
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: drawersCast.open ? "var(--ac)" : "var(--tp)",
                      opacity: drawersCast.open ? 1 : 0.6,
                    }}
                    onClick={() => {
                      const open = !drawersCast.open;
                      setDrawersCast((p) => ({ seq: p.seq + 1, open }));
                      /* 111 §2 — OPEN only what HAS subtasks (the counter's own
                         fact: non-archived rows, SubtaskCounter.tsx:85); an
                         empty drawer opened in bulk is noise. CLOSE stays
                         total. Docs/notes gate themselves in the row's tick
                         handler on the same principle. */
                      setTkExpanded(
                        open
                          ? new Set(
                              ctk
                                .filter(
                                  (x: Task) =>
                                    (Array.isArray(x.subtasks)
                                      ? x.subtasks
                                      : []
                                    ).filter(
                                      (s: { archived?: boolean } | null) =>
                                        s && !s.archived,
                                    ).length > 0,
                                )
                                .map((x: Task) => x.id),
                            )
                          : new Set(),
                      );
                    }}
                  >
                    {drawersCast.open ? (
                      <ChevronsDownUp size={14} />
                    ) : (
                      <ChevronsUpDown size={14} />
                    )}
                  </button>
                </>
              }
              rightContent={
                <>
                  {canEditTasks && !tkLocked && (
                    <AddButton
                      label={t("common.add_button.add", {
                        fallback: "Add",
                      })}
                      onClick={() =>
                        openTaskModal("tk", {
                          ...(isCc ? { ccId: cs.id } : { caseId: cs.id }),
                          status: "todo",
                          reminder: "1",
                          _caseFixed: true,
                        })
                      }
                    />
                  )}
                  <LockButton
                    locked={tkLocked}
                    onToggle={() => setTkLocked((v: boolean) => !v)}
                  />
                </>
              }
            />
            {/* 210 §1 — while the tasks could not load, the body says so in its
                place (ListNotLoaded) — never « No tasks yet ». */}
            <ListNotLoaded lists={[SK.tk]}>
            {/* 190 glance (ii) → round 2 [A] — the case Bento, in the
                matrix's slot, spanning both bodies (`.bentoPane`). */}
            {bentoOpen && canBento && (
              <div ref={paneRef} className="bentoPane">
                <BentoDialog
                  frame="panel"
                  open={bentoOpen}
                  onClose={closeBento}
                  surface="case"
                  bento={caseBento}
                  quadrantOf={userMatrixQuadrant}
                  title={t("bento.case_title", { fallback: "Case Bento" })}
                  tasks={tkFil}
                  cases={ca as { id: number; title?: string; itemLinks?: unknown }[]}
                  collectiveCases={cc as { id: number; itemLinks?: unknown }[]}
                  tkTypes={tkTypes as { code?: string | null; desc?: string }[]}
                  canMove={canBento}
                  onCardClick={(tk) => { setBentoOpen(false); queueScrollToTk(tk.id); }}
                  t={t}
                />
              </div>
            )}
            {/* BRIEF 86 PHASE 2 STEP 4 — `!tkMatrix`, as CCV has it.
                Without it the "no tasks yet" sentence rendered UNDER an
                open matrix, which draws its own empty state. (ii): the
                Bento panel draws its own too. */}
            {!tkMatrix && !bentoOpen && ctk.length === 0 && (
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  marginBottom: 8,
                }}
              >
                {t("case.empty.no_tasks_yet")}
              </p>
            )}
            {!tkMatrix && !bentoOpen && ctk.length > 0 && tkFil.length === 0 && (
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  marginBottom: 8,
                }}
              >
                {t("case.empty.no_tasks_match")}
              </p>
            )}
            {/* BRIEF 104 §1a — `priMap` no longer passed; the matrix draws no
                level letter. */}
            {tkMatrix && canMatrix && (
              <TaskMatrix
                tasks={tkFil}
                ca={ca}
                cc={cc} // 111 §5c — itemLinks feed for cc rows
                showMatter={false} // 191 C.0 — a case's own matrix shows no case name on its cards
                tkTypes={tkTypes}
                onMove={(tkId: number, q: string) => {
                  const tt = tk.find((x: Person) => x.id === tkId);
                  if (tt) svTkData({ ...tt, caseEisenhower: q });
                }}
                onTaskClick={(t: Task) => {
                  /* BRIEF 86 PHASE 2 STEP 4 — the union of the two
                     guards, and it is NOT symmetric.

                     CaseView had the CONDITION (don't navigate to the
                     matter you are already on) and a bare `nav`;
                     CollectiveCaseView had C-1's GUARDED navigator
                     `_navCase` and no condition. Each held one half, so
                     CaseView takes `_navCase` — it already computes it
                     at :659 and used it nowhere here.

                     CCV does NOT take the condition, deliberately.
                     There `cs.id` is a COLLECTIVE case id and
                     `t.caseId` a case id: two independent
                     autoincrements, so `t.caseId !== cs.id` is
                     comparing across namespaces and would suppress a
                     real navigation whenever the two numbers happened
                     to coincide — the id-collision class the
                     2026-07-13 fix pass cleaned out of the financial
                     filters. On a collective case a task carrying a
                     caseId is ALWAYS from another matter, so the
                     condition has nothing to add there. */
                  if (t.caseId && t.caseId !== cs.id)
                    _navCase("ca", t.caseId);
                }}
                onClose={() => setTkMatrix(false)}
              />
            )}
            {!tkMatrix && !bentoOpen && tkFil.length > 0 && (
              <div className="table-wrap" style={{ marginBottom: 12 }}>
                <Table>
                  <TaskTableHead t={t} fur={fur} showLevel={__tkShowLevel} sort={__tkCur} onSort={__cycleTkSort} />
                  <tbody>
                    {tkPaged.map((t: Task) => (
                      <CaseTaskRow
                        showLevel={__tkShowLevel}
                        key={t.id}
                        /* BRIEF 98 §4 — the matter this section is already
                           rendering, forwarded so the documents add row can
                           offer the OneDrive picker at the matter's own folder.
                           Nothing is derived here and nothing new is fetched:
                           `cs` is this component's own subject. */
                        oneDriveMatter={{ kind: kind === "cc" ? "cc" : "ca", matter: cs }}
                        matterRow={cs} // 111 §5c — itemLinks feed
                        drawersCast={drawersCast} // 110 §1h
                        t={t}
                        co={co}
                        currentUser={currentUser}
                        tk={tk}
                        tkTypes={tkTypes}
                        itemLevelList={itemLevelList}
                        priMap={_ITEM_LEVEL}
                        activeTimers={activeTimers}
                        tkLocked={tkLocked}
                        canEditOthersTasks={canEditOthersTasks}
                        permIsAdmin={!!_permViewer?.isAdmin}
                        isRP={_isRPHere}
                        tkExpanded={tkExpanded}
                        setTkExpanded={setTkExpanded}
                        TkStatusTag={TkStatusTag}
                        startTimer={startTimer}
                        svTkData={svTkData}
                        sTk={sTk}
                        onSubtaskDone={onSubtaskDone}
                        openModal={openTaskModal}
                        onDeleteTask={(_t: Task) =>
                          confirmAndUndo({
                            label: "this task",
                            archive: true,
                            toastName: `Archived "${_t.title || "task"}"`,
                            onConfirm: () =>
                              s(
                                SK.tk,
                                tk.filter((x: Person) => x.id !== _t.id),
                                sTk,
                              ),
                            undo: {
                              snapshot: _t,
                              restore: (snap: Task) =>
                                s(SK.tk, [...tkRef.current, snap], sTk),
                            },
                          })
                        }
                      />
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {!tkMatrix && !bentoOpen && tkTotalPages > 1 && (
              <Pager cur={tkCurPage} tot={tkTotalPages} setPage={setTkPage} />
            )}
            </ListNotLoaded>
          </>
        );
      })()}
    </div>
  );
}
